"use client";

import { MotionConfig } from "framer-motion";
import { useRouter } from "next/navigation";
import { useCallback, useEffect, useRef, useState } from "react";

import { FetchToastProvider } from "@/components/providers/FetchToastProvider";

import { EstimatorProvider } from "../../_context/EstimatorContext";
import { EstimatorSourcesProvider } from "../../_context/EstimatorSourcesContext";
import { useEstimateGate } from "../../_hooks/useEstimateGate";
import { SURFACE_FEEL } from "../../_logic/motion";
import { saveEstimatorLead, signupHref } from "../../_logic/public/lead";
import { sampleSources, type SampleEstimate } from "../../_logic/public/sample-estimate";
import { TripBar } from "../trip/TripBar";
import { ComparisonTable } from "../workspace/ComparisonTable";
import { EstimateLockOverlay } from "./EstimateLockOverlay";

/**
 * The dashboard's estimator on a public page, opened on a priced sample. It answers only from
 * the sample: fares from its finished search, hotel rates from its sources, and nothing that
 * needs an org (saving, the assistant, events) is mounted. The first click, key or focus inside
 * blurs the results and asks for an email, then sends the visitor to sign up.
 * @param props.sample - The sample estimate, built once on the server so both renders agree.
 * @returns The public estimator.
 * @throws Never.
 */
export const PublicCostEstimator = ({ sample }: { sample: SampleEstimate }) => {
  // Hooks
  const router = useRouter();
  const continueWith = useCallback(
    async (email: string) => {
      await saveEstimatorLead(email);
      router.push(signupHref(email));
    },
    [router]
  );
  const { gate, boundary, emailInput, submit } = useEstimateGate(continueWith);
  const tripBar = useRef<HTMLDivElement>(null);
  const comparison = useRef<HTMLDivElement>(null);

  // States
  const [sources] = useState(() => sampleSources(sample.hotels));

  // Derived
  const locked = gate.status !== "preview";

  // Effects
  // Inert takes the locked sample out of tab order and the accessibility tree, so Shift+Tab from
  // the email field moves on instead of landing in the sample and bouncing back. Set on the node
  // because React 18 drops a boolean `inert` prop.
  useEffect(() => {
    for (const node of [tripBar.current, comparison.current]) if (node) node.inert = locked;
  }, [locked]);

  // Render
  return (
    <FetchToastProvider>
      <EstimatorSourcesProvider value={sources}>
        <EstimatorProvider saved={sample}>
          <MotionConfig reducedMotion="user">
            <div className={`flex flex-col gap-6 @container ${SURFACE_FEEL}`}>
              <header className="flex flex-col gap-1">
                <p className="text-sm font-medium text-gray-600">Sample estimate</p>
                <h2 className="text-2xl font-medium tracking-tight text-gray-900 md:text-3xl">
                  {sample.workspace.title}
                </h2>
              </header>
              <div ref={tripBar} {...boundary}>
                <TripBar />
              </div>
              <div className="relative min-h-[420px]">
                <div
                  ref={comparison}
                  {...boundary}
                  className={`transition-[filter] duration-300 motion-reduce:transition-none ${locked ? "select-none blur-[6px]" : ""}`}
                >
                  <ComparisonTable />
                </div>
                {locked && <EstimateLockOverlay gate={gate} emailInput={emailInput} onSubmit={submit} />}
              </div>
            </div>
          </MotionConfig>
        </EstimatorProvider>
      </EstimatorSourcesProvider>
    </FetchToastProvider>
  );
};
